import React, { useState } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import logger from '../utils/logger';

const TopUpModal = ({ isOpen, onClose, currentTier = 'free', currentUsage }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTopUp, setSelectedTopUp] = useState(null);

  // Top-up options by tier - STRICTLY ENFORCED
  const topUpOptions = {
    free: [], // No top-ups for free tier
    starter: [
      {
        id: 'starter',
        name: 'Starter Credit Pack',
        credits: 600,
        price: '$5.00',
        savings: '20% off',
        description: '600 additional images',
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID_TOPUP_STARTER
      }
    ],
    professional: [
      {
        id: 'professional',
        name: 'Professional Credit Pack',
        credits: 1200,
        price: '$8.00',
        savings: '30% off',
        description: '1,200 additional images',
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID_TOPUP_PROFESSIONAL
      }
    ],
    business: [
      {
        id: 'business',
        name: 'Business Credit Pack',
        credits: 3000,
        price: '$20.00',
        savings: '40% off',
        description: '3,000 additional images',
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID_TOPUP_BUSINESS
      }
    ],
    enterprise: [], // No top-ups for enterprise - custom arrangements
    admin: [] // No top-ups for admin
  };

  // Get available top-ups for current tier
  const availableTopUps = topUpOptions[currentTier] || [];

  const handlePurchaseTopUp = async (topUpId) => {
    setLoading(true);
    setSelectedTopUp(topUpId);
    
    try {
      const response = await api.billing.createTopUpCheckout(topUpId);
      
      if (response.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      logger.error('Error creating top-up checkout:', error);
      toast.error('Failed to start checkout process. Please try again.');
      setLoading(false);
      setSelectedTopUp(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="border-b border-border p-4 sm:p-6">
          <div className="flex items-start sm:items-center justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primaryText">Buy Additional Credits</h2>
              <p className="text-sm sm:text-base text-secondaryText mt-1">
                Add more images to your current billing period
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-button rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-secondaryText" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {availableTopUps.length === 0 ? (
            <div className="text-center py-12">
              {currentTier === 'free' ? (
                <>
                  <svg className="w-16 h-16 mx-auto text-secondaryText/50 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-primaryText mb-2">
                    Top-ups Not Available
                  </h3>
                  <p className="text-secondaryText mb-6">
                    Upgrade to a paid plan to purchase additional credits
                  </p>
                  <button
                    onClick={() => {
                      onClose();
                      // You might want to open the plans modal here
                    }}
                    className="btn-primary"
                  >
                    View Plans
                  </button>
                </>
              ) : currentTier === 'enterprise' ? (
                <>
                  <svg className="w-16 h-16 mx-auto text-accent/50 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-primaryText mb-2">
                    Custom Solutions Available
                  </h3>
                  <p className="text-secondaryText">
                    Contact your account manager for additional credits or custom pricing
                  </p>
                </>
              ) : (
                <>
                  <p className="text-secondaryText">
                    No top-up options available for your plan
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Current Usage Stats */}
              {currentUsage && (
                <div className="mb-6 p-4 bg-background rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-secondaryText">Current Period Usage</span>
                    <span className="text-sm font-medium text-primaryText">
                      {currentUsage.used} / {currentUsage.limit} images
                    </span>
                  </div>
                  <div className="w-full bg-button rounded-full h-2">
                    <div 
                      className="bg-accent h-2 rounded-full transition-all"
                      style={{ width: `${(currentUsage.used / currentUsage.limit) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-secondaryText mt-2">
                    {currentUsage.remaining} images remaining this month
                  </p>
                </div>
              )}

              {/* Available Top-ups */}
              <div className="space-y-4">
                {availableTopUps.map((topUp) => (
                  <div
                    key={topUp.id}
                    className="border-2 border-border hover:border-accent/50 rounded-xl p-6 transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-primaryText mb-1">
                          {topUp.name}
                        </h3>
                        <p className="text-secondaryText">{topUp.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primaryText">{topUp.price}</p>
                        <p className="text-sm text-accent font-medium">{topUp.savings}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center text-sm text-secondaryText">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {topUp.credits.toLocaleString()} credits
                        </div>
                        <div className="flex items-center text-sm text-secondaryText">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Valid this billing period
                        </div>
                      </div>

                      <button
                        onClick={() => handlePurchaseTopUp(topUp.id)}
                        disabled={loading}
                        className={`px-6 py-2 rounded-lg font-medium transition-all ${
                          loading && selectedTopUp === topUp.id
                            ? 'bg-button text-secondaryText cursor-not-allowed'
                            : 'bg-accent text-black hover:bg-accent/90'
                        }`}
                      >
                        {loading && selectedTopUp === topUp.id ? (
                          <span className="flex items-center">
                            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          'Purchase'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-accent/10 border border-accent/30 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-accent mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm">
                    <p className="font-medium text-primaryText mb-1">How top-ups work:</p>
                    <ul className="text-secondaryText space-y-1">
                      <li>• Credits are added to your account immediately</li>
                      <li>• Unused credits roll over to next month with active subscription</li>
                      <li>• You can purchase multiple top-ups anytime</li>
                      <li>• Top-ups are specific to your subscription tier</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopUpModal;